import React, { useState } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  EyeOff, 
  Camera, 
  DollarSign, 
  Loader2,
  Search,
  ListChecks,
} from 'lucide-react';
import { useMyServices, useCreateVendorService, useUpdateVendorService, useDeleteVendorService, useToggleServiceStatus } from '../../../services/vendorServiceService';
import { useToastContext } from '../../../contexts/ToastContext';
import { useAuth } from '../../../contexts/AuthContext';
import Button from '../../../components/shared/Button';
import Input from '../../../components/shared/Input';
import SearchSelect from '../../../components/shared/SearchSelect';
import { ServiceModal } from '../../../components/vendor';
import { useVendorSubscriptionStatus } from '../../../services/subscriptionService';
import { VendorPageHeader, VendorGuideCallout } from '../../../components/vendor/guidance';
import { VENDOR_PAGE_GUIDES } from '../../../utils/vendorPageGuides';

const parseSpecialties = (specialties) => {
  if (!specialties) return [];
  if (Array.isArray(specialties)) return specialties;
  if (typeof specialties === 'string') {
    try {
      const parsed = JSON.parse(specialties);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

const VendorServices = () => {
  const { user } = useAuth();
  const { success, error: showError } = useToastContext();
  const [modalState, setModalState] = useState({
    isOpen: false,
    isEdit: false,
    selectedService: null
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Form state
  const [formData, setFormData] = useState({
    service_name: '',
    image_url: '',
    description: '',
    search_tags: '',
    service_category_id: '',
    base_price: '',
    is_featured: false,
    sort_order: 0
  });
// console.log('====================================');
// console.log(formData);
// console.log('====================================');
  // Fetch data
  const { data: servicesData, isLoading: servicesLoading, refetch } = useMyServices();
  const { data: statusResponse } = useVendorSubscriptionStatus();

  // Mutations
  const createServiceMutation = useCreateVendorService({
    onSuccess: () => {
      success('Service created successfully!');
      closeModal();
      resetForm();
      refetch();
    },
    onError: (error) => {
      showError(error?.response?.data?.message || 'Failed to create service');
    }
  });

  const updateServiceMutation = useUpdateVendorService({
    onSuccess: () => {
      success('Service updated successfully!');
      closeModal();
      resetForm();
      refetch();
    },
    onError: (error) => {
      showError(error?.response?.data?.message || 'Failed to update service');
    }
  });

  const deleteServiceMutation = useDeleteVendorService({
    onSuccess: () => {
      success('Service deleted successfully!');
      refetch();
    },
    onError: (error) => {
      showError(error?.response?.data?.message || 'Failed to delete service');
    }
  });

  const toggleStatusMutation = useToggleServiceStatus({
    onSuccess: () => {
      success('Service status updated successfully!');
      refetch();
    },
    onError: (error) => {
      showError(error?.response?.data?.message || 'Failed to update service status');
    }
  });

  // Handle paginated response from API
  const services = Array.isArray(servicesData?.data?.data) ? servicesData.data.data : [];
  
  // Get categories from vendor profile (selected during verification)
  const categories = user?.vendor_profile?.categories || [];
  const vendorSpecialties = parseSpecialties(user?.vendor_profile?.specialties);
  const usageLimits = statusResponse?.data?.current_subscription?.limits || {};
  const usage = statusResponse?.data?.current_subscription?.usage || {};
  const serviceLimit = usageLimits?.max_services ?? null;
  const currentServiceCount = usage?.services_count ?? services.length;
  const atServiceLimit = Boolean(statusResponse?.data?.current_subscription?.at_service_limit);
  const imageLimit = usageLimits?.max_images ?? null;
  const currentImageCount = usage?.images_count ?? 0;

  // Filter services
  const filteredServices = services.filter(service => {
    const matchesSearch = service.service_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         service.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !selectedCategory || service.service_category_id === selectedCategory;
    const matchesStatus = statusFilter === 'all' || 
                         (statusFilter === 'active' && service.is_active) ||
                         (statusFilter === 'inactive' && !service.is_active);
    
    return matchesSearch && matchesCategory && matchesStatus;
  });

  const resetForm = () => {
    setFormData({
      service_name: '',
      image_url: '',
      description: '',
      search_tags: '',
      service_category_id: '',
      base_price: '',
      is_featured: false,
      sort_order: 0
    });
  };

  const openCreateModal = () => {
    setModalState({ isOpen: true, isEdit: false, selectedService: null });
    resetForm();
  };

  const openEditModal = (service) => {
    const allowedSet = new Set(vendorSpecialties);
    const selectedTags = (service.search_tags || '')
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean)
      .filter((tag) => allowedSet.has(tag));

    setModalState({ isOpen: true, isEdit: true, selectedService: service });
    setFormData({
      service_name: service.service_name || '',
      image_url: service.image_url || '',
      description: service.description || '',
      search_tags: selectedTags.join(', '),
      service_category_id: service.service_category_id || '',
      base_price: service.base_price || '',
      is_featured: service.is_featured || false,
      sort_order: service.sort_order || 0
    });
  };

  const closeModal = () => {
    setModalState({ isOpen: false, isEdit: false, selectedService: null });
    resetForm();
  };

  const handleCreate = () => {
    if (categories.length === 0) {
      showError('You need to select service categories during verification before creating services. Please complete your verification process.');
      return;
    }
    openCreateModal();
  };

  const handleEdit = (service) => {
    openEditModal(service);
  };

  const handleDelete = (service) => {
    if (window.confirm('Are you sure you want to delete this service?')) {
      deleteServiceMutation.mutate(service.id);
    }
  };

  const handleToggleStatus = (service) => {
    toggleStatusMutation.mutate(service.id);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (modalState.isEdit) {
      updateServiceMutation.mutate({ id: modalState.selectedService.id, data: formData });
    } else {
      createServiceMutation.mutate(formData);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleImageSelect = (image) => {
    if (image) {
      handleInputChange('image_url', image.image_url);
    }
  };


  const getCategoryName = (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId);
    return category?.category_name || 'Unknown Category';
  };

  if (servicesLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading services...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
        <VendorPageHeader
          title="Services"
          subtitle={
            <>
              {categories.length === 0
                ? 'Complete verification to select service categories and start creating services'
                : 'Manage your service offerings'}
              <span className="block mt-1 text-gray-500">
                Services: {currentServiceCount}
                {serviceLimit === null ? ' / unlimited' : ` / ${serviceLimit}`} | Images:{' '}
                {currentImageCount}
                {imageLimit === null ? ' / unlimited' : ` / ${imageLimit}`}
              </span>
            </>
          }
          actions={
            <Button
              onClick={handleCreate}
              icon={Plus}
              className="w-full sm:w-auto"
              disabled={categories.length === 0 || atServiceLimit}
              title={
                categories.length === 0
                  ? 'Complete verification to select service categories first'
                  : atServiceLimit
                    ? "You have reached your plan's service limit"
                    : ''
              }
            >
              Add New Service
            </Button>
          }
        />

        <VendorGuideCallout
          title={VENDOR_PAGE_GUIDES.services.title}
          steps={VENDOR_PAGE_GUIDES.services.steps}
          icon={ListChecks}
        />

        {categories.length === 0 && (
          <p className="text-sm text-amber-600 -mt-2 mb-4">
            You need to complete verification and select service categories first
          </p>
        )}
        {categories.length > 0 && atServiceLimit && (
          <p className="text-sm text-amber-600 -mt-2 mb-4">
            You have reached your service limit for the current plan. Upgrade to add more services.
          </p>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  type="text"
                  placeholder="Search services..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
              <SearchSelect
                value={selectedCategory}
                onChange={setSelectedCategory}
                options={[
                  { value: '', label: 'All Categories' },
                  ...categories.map(category => ({
                    value: category.id,
                    label: category.category_name
                  }))
                ]}
                placeholder="Filter by category"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <SearchSelect
                value={statusFilter}
                onChange={setStatusFilter}
                options={[
                  { value: 'all', label: 'All Status' },
                  { value: 'active', label: 'Active' },
                  { value: 'inactive', label: 'Inactive' }
                ]}
                placeholder="Filter by status"
              />
            </div>
          </div>
        </div>

        {/* Services Grid */}
        {filteredServices.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <Camera className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Services Found</h3>
            <p className="text-gray-600 mb-6">
              {services.length === 0 
                ? categories.length === 0
                  ? "You need to select service categories during verification before creating services."
                  : "You haven't created any services yet. Get started by adding your first service!"
                : "No services match your current filters. Try adjusting your search criteria."
              }
            </p>
            {services.length === 0 && categories.length > 0 && (
              <Button onClick={handleCreate} icon={Plus} disabled={atServiceLimit}>
                Create Your First Service
              </Button>
            )}
            {services.length === 0 && categories.length === 0 && (
              <div className="space-y-2">
                <p className="text-sm text-gray-500">
                  Please complete your vendor verification and select service categories first.
                </p>
                <Button 
                  onClick={() => window.location.href = '/vendor/verification'} 
                  variant="outline"
                >
                  Complete Verification
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredServices.map((service) => (
              <div key={service.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                {/* Service Image */}
                <div className="aspect-w-16 aspect-h-9 bg-gray-100 h-48">
                  {service.image_url ? (
                    <img
                      src={service.image_url}
                      alt={service.service_name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Camera className="w-12 h-12 text-gray-400" />
                    </div>
                  )}
                </div>

                {/* Service Content */}
                <div className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="text-lg font-semibold text-gray-900 truncate">
                      {service.service_name}
                    </h3>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      service.is_active 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {service.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                    {service.description}
                  </p>
                  
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center text-sm text-gray-600">
                      <span className="font-medium mr-2">Category:</span>
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                        {getCategoryName(service.service_category_id)}
                      </span>
                    </div>
                    
                    <div className="flex items-center text-sm text-gray-600">
                      <DollarSign className="w-4 h-4 mr-2" />
                      <span className="font-medium">${service.base_price}</span>
                    </div>
                    
                   
                    
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                    <div className="flex items-center space-x-2">
                      <Button
                        onClick={() => handleEdit(service)}
                        variant="outline"
                        size="sm"
                        icon={Edit}
                        className="text-blue-600 border-blue-600 hover:bg-blue-50"
                      >
                        Edit
                      </Button>
                      <Button
                        onClick={() => handleToggleStatus(service)}
                        variant="outline"
                        size="sm"
                        icon={service.is_active ? EyeOff : Eye}
                        className={service.is_active 
                          ? "text-yellow-600 border-yellow-600 hover:bg-yellow-50"
                          : "text-green-600 border-green-600 hover:bg-green-50"
                        }
                      >
                        {service.is_active ? 'Hide' : 'Show'}
                      </Button>
                    </div>
                    
                    <Button
                      onClick={() => handleDelete(service)}
                      variant="outline"
                      size="sm"
                      icon={Trash2}
                      className="text-red-600 border-red-600 hover:bg-red-50"
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Service Modal */}
        <ServiceModal
          isOpen={modalState.isOpen}
          isEdit={modalState.isEdit}
          formData={formData}
          categories={categories}
          specialties={vendorSpecialties}
          onClose={closeModal}
          onSubmit={handleSubmit}
          onInputChange={handleInputChange}
          onImageSelect={handleImageSelect}
          maxImages={imageLimit}
          isLoading={createServiceMutation.isPending || updateServiceMutation.isPending}
        />
    </div>
  );
};

export default VendorServices;
